import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdSave, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import '../master/MasterData.css';

interface MenuConfig {
  menuId: string;
  menuName: string;
  description: string;
  isVisible: boolean;
  isLocked: boolean;
  subMenus?: SubMenuConfig[];
}

interface SubMenuConfig {
  subMenuId: string;
  subMenuName: string;
  isVisible: boolean;
}

const MenuSetup: React.FC = () => {
  const [menuConfigs, setMenuConfigs] = useState<MenuConfig[]>([
    {
      menuId: 'dashboard',
      menuName: 'Dashboard',
      description: 'Main dashboard and overview',
      isVisible: true,
      isLocked: false,
      subMenus: []
    },
    {
      menuId: 'master',
      menuName: 'Master Data',
      description: 'Chart of accounts, customers, vendors, items, etc.',
      isVisible: true,
      isLocked: true,
      subMenus: []
    },
    {
      menuId: 'sales',
      menuName: 'Sales / AR',
      description: 'Sales transactions and accounts receivable management',
      isVisible: true,
      isLocked: false,
      subMenus: [
        { subMenuId: 'quotations', subMenuName: 'Sales Quotations', isVisible: true },
        { subMenuId: 'orders', subMenuName: 'Sales Orders', isVisible: true },
        { subMenuId: 'deliveries', subMenuName: 'Delivery Receipts', isVisible: true },
        { subMenuId: 'invoices', subMenuName: 'Sales Invoices', isVisible: true },
        { subMenuId: 'collections', subMenuName: 'Collections', isVisible: true },
        { subMenuId: 'ar-aging', subMenuName: 'AR Aging Report', isVisible: true }
      ]
    },
    {
      menuId: 'purchases',
      menuName: 'Purchases / AP',
      description: 'Purchase transactions and accounts payable management',
      isVisible: true,
      isLocked: false,
      subMenus: [
        { subMenuId: 'po', subMenuName: 'Purchase Orders', isVisible: true },
        { subMenuId: 'receipts', subMenuName: 'Goods Receipt', isVisible: true },
        { subMenuId: 'pi', subMenuName: 'Purchase Invoices', isVisible: true },
        { subMenuId: 'payments', subMenuName: 'Payments', isVisible: true },
        { subMenuId: 'ap-aging', subMenuName: 'AP Aging Report', isVisible: true }
      ]
    },
    {
      menuId: 'inventory',
      menuName: 'Inventory',
      description: 'Inventory and stock management',
      isVisible: true,
      isLocked: false,
      subMenus: [
        { subMenuId: 'status', subMenuName: 'Stock Status', isVisible: true },
        { subMenuId: 'transactions', subMenuName: 'Stock In/Out', isVisible: true },
        { subMenuId: 'adjustment', subMenuName: 'Stock Adjustment', isVisible: true },
        { subMenuId: 'bom', subMenuName: 'Bill of Materials', isVisible: true }
      ]
    },
    {
      menuId: 'gl',
      menuName: 'General Ledger',
      description: 'General ledger and financial accounting',
      isVisible: true,
      isLocked: false,
      subMenus: [
        { subMenuId: 'journal', subMenuName: 'Journal Entry', isVisible: true },
        { subMenuId: 'reconciliation', subMenuName: 'Bank Reconciliation', isVisible: true },
        { subMenuId: 'statements', subMenuName: 'Financial Statements', isVisible: true }
      ]
    },
    {
      menuId: 'payroll',
      menuName: 'Payroll',
      description: 'Payroll processing and government reporting',
      isVisible: true,
      isLocked: false,
      subMenus: [
        { subMenuId: 'timekeeping', subMenuName: 'Timekeeping', isVisible: true },
        { subMenuId: 'processing', subMenuName: 'Payroll Processing', isVisible: true },
        { subMenuId: 'register', subMenuName: 'Payroll Register', isVisible: true },
        { subMenuId: 'gov-reports', subMenuName: 'Government Reports', isVisible: true }
      ]
    },
    {
      menuId: 'reports',
      menuName: 'Reports',
      description: 'Business intelligence and reporting',
      isVisible: true,
      isLocked: false,
      subMenus: [
        { subMenuId: 'sales-reports', subMenuName: 'Sales Reports', isVisible: true },
        { subMenuId: 'expense-reports', subMenuName: 'Expense Reports', isVisible: true },
        { subMenuId: 'tax-reports', subMenuName: 'Tax Reports', isVisible: true },
        { subMenuId: 'gl-reports', subMenuName: 'General Ledger Reports', isVisible: true }
      ]
    },
    {
      menuId: 'settings',
      menuName: 'Settings',
      description: 'System settings and configuration',
      isVisible: true,
      isLocked: false,
      subMenus: [
        { subMenuId: 'users', subMenuName: 'User Management', isVisible: true },
        { subMenuId: 'company', subMenuName: 'Company Profile', isVisible: true },
        { subMenuId: 'fiscal', subMenuName: 'Fiscal Year Setup', isVisible: true },
        { subMenuId: 'menu', subMenuName: 'Menu Setup', isVisible: true }
      ]
    }
  ]);

  const [hasChanges, setHasChanges] = useState(false);

  const handleToggleMenu = (menuId: string) => {
    const menu = menuConfigs.find(m => m.menuId === menuId);
    if (menu?.isLocked) {
      alert('This menu is locked and cannot be hidden.');
      return;
    }

    setMenuConfigs(menuConfigs.map(menu =>
      menu.menuId === menuId ? { ...menu, isVisible: !menu.isVisible } : menu
    ));
    setHasChanges(true);
  };

  const handleToggleSubMenu = (menuId: string, subMenuId: string) => {
    setMenuConfigs(menuConfigs.map(menu =>
      menu.menuId === menuId && menu.subMenus
        ? {
            ...menu,
            subMenus: menu.subMenus.map(sub =>
              sub.subMenuId === subMenuId ? { ...sub, isVisible: !sub.isVisible } : sub
            )
          }
        : menu
    ));
    setHasChanges(true);
  };

  const handleSave = () => {
    alert('Menu configuration saved successfully!');
    setHasChanges(false);
    // TODO: Save to API
  };

  const handleReset = () => {
    if (window.confirm('Reset all menu settings to default?')) {
      setMenuConfigs(menuConfigs.map(menu => ({
        ...menu,
        isVisible: true,
        subMenus: menu.subMenus?.map(sub => ({ ...sub, isVisible: true }))
      })));
      setHasChanges(true);
    }
  };

  const visibleCount = menuConfigs.filter(m => m.isVisible).length;
  const hiddenCount = menuConfigs.filter(m => !m.isVisible).length;
  const totalSubMenus = menuConfigs.reduce((sum, menu) => sum + (menu.subMenus?.length || 0), 0);
  const visibleSubMenus = menuConfigs.reduce((sum, menu) =>
    sum + (menu.subMenus?.filter(sub => sub.isVisible).length || 0), 0
  );

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Menu Setup</h2>
            <p className="subtitle">Control menu visibility and navigation options</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={handleReset}>
              Reset to Default
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!hasChanges}
            >
              <MdSave /> Save Changes
            </button>
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">📋</div>
            <div className="summary-content">
              <p className="summary-label">Total Main Menus</p>
              <p className="summary-value">{menuConfigs.length}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">✅</div>
            <div className="summary-content">
              <p className="summary-label">Visible Menus</p>
              <p className="summary-value" style={{ color: '#28a745' }}>{visibleCount}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">👁️</div>
            <div className="summary-content">
              <p className="summary-label">Hidden Menus</p>
              <p className="summary-value" style={{ color: '#dc3545' }}>{hiddenCount}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📑</div>
            <div className="summary-content">
              <p className="summary-label">Visible Sub-Menus</p>
              <p className="summary-value">{visibleSubMenus} / {totalSubMenus}</p>
            </div>
          </div>
        </div>

        <div className="page-content">
          {hasChanges && (
            <div style={{
              padding: '15px',
              backgroundColor: '#fff3cd',
              borderRadius: '8px',
              border: '1px solid #ffc107',
              marginBottom: '20px'
            }}>
              <strong>⚠️ Unsaved Changes</strong>
              <p style={{ margin: '5px 0 0 0' }}>
                You have unsaved menu configuration changes. Click "Save Changes" to apply them.
              </p>
            </div>
          )}

          <div style={{
            padding: '20px',
            backgroundColor: '#e3f2fd',
            borderRadius: '8px',
            border: '1px solid #1a237e',
            marginBottom: '30px'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#1a237e' }}>Important Notes</h4>
            <ul style={{ margin: '0', paddingLeft: '20px', color: '#666' }}>
              <li><strong>Master Data</strong> menu cannot be hidden (locked for system integrity)</li>
              <li>Hidden menus will not appear in the navigation sidebar</li>
              <li>Users will not be able to access hidden menu functions</li>
              <li>Changes will be applied immediately after saving</li>
            </ul>
          </div>

          {menuConfigs.map(menu => (
            <div
              key={menu.menuId}
              style={{
                marginBottom: '20px',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: menu.isVisible ? '#fff' : '#f8f9fa'
              }}
            >
              {/* Main Menu Header */}
              <div style={{
                padding: '20px',
                backgroundColor: menu.isLocked ? '#fff3cd' : (menu.isVisible ? '#1a237e' : '#6c757d'),
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 5px 0', color: menu.isLocked ? '#1a237e' : '#fff' }}>
                    {menu.menuName}
                    {menu.isLocked && <span style={{ marginLeft: '10px', fontSize: '14px' }}>🔒 Locked</span>}
                  </h4>
                  <p style={{ margin: 0, fontSize: '13px', opacity: 0.9, color: menu.isLocked ? '#666' : '#fff' }}>
                    {menu.description}
                  </p>
                </div>
                <button
                  className="btn"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: menu.isLocked ? '#e0e0e0' : (menu.isVisible ? '#fff' : '#28a745'),
                    color: menu.isLocked ? '#999' : (menu.isVisible ? '#1a237e' : '#fff'),
                    border: 'none',
                    borderRadius: '6px',
                    cursor: menu.isLocked ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 'bold'
                  }}
                  onClick={() => handleToggleMenu(menu.menuId)}
                  disabled={menu.isLocked}
                >
                  {menu.isVisible ? (
                    <>
                      <MdVisibility /> Visible
                    </>
                  ) : (
                    <>
                      <MdVisibilityOff /> Hidden
                    </>
                  )}
                </button>
              </div>

              {/* Sub-Menus */}
              {menu.subMenus && menu.subMenus.length > 0 && (
                <div style={{ padding: '20px' }}>
                  <h5 style={{ marginBottom: '15px', color: '#1a237e' }}>Sub-Menu Items</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    {menu.subMenus.map(subMenu => (
                      <div
                        key={subMenu.subMenuId}
                        style={{
                          padding: '12px 15px',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: subMenu.isVisible ? '#fff' : '#f8f9fa'
                        }}
                      >
                        <span style={{
                          fontWeight: subMenu.isVisible ? 'bold' : 'normal',
                          color: subMenu.isVisible ? '#1a237e' : '#6c757d'
                        }}>
                          {subMenu.subMenuName}
                        </span>
                        <button
                          className="btn-icon"
                          onClick={() => handleToggleSubMenu(menu.menuId, subMenu.subMenuId)}
                          title={subMenu.isVisible ? 'Hide' : 'Show'}
                          style={{
                            color: subMenu.isVisible ? '#28a745' : '#dc3545',
                            fontSize: '20px'
                          }}
                        >
                          {subMenu.isVisible ? <MdVisibility /> : <MdVisibilityOff />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </MainLayout>
  );
};

export default MenuSetup;
